// app/src/pages/PaymentMethodsPage.tsx
import { useEffect, useState, useMemo, useCallback } from 'react';
import { Preferences } from '@capacitor/preferences';
import { SecureStorage } from '../../storage/SecureStorage';
import api from '../../api/Client';
import { useNavigate } from 'react-router-dom';
import { CreditCard, RefreshCw, AlertCircle, ArrowLeft } from 'lucide-react';
import PaymentWebView from '../../components/PaymentWebView';

type StoredUser = { id: number; fullName: string };
type PaymentMethod = {
  id: number;
  provider: 'addon' | string;
  payer_ref: string | null;
  pmt_ref: string | null;     // token para rebill
  brand: string | null;       // VISA/MC/…
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
  is_default?: boolean;
  created_at?: string;
};

export default function PaymentMethodsPage() {
  const [user, setUser] = useState<StoredUser | null>(null);
  const [items, setItems] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [paymentHtml, setPaymentHtml] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let currentUser: StoredUser | null = await SecureStorage.getUser?.() ?? null;
      if (!currentUser) {
        const { value } = await Preferences.get({ key: 'user' });
        if (value) currentUser = JSON.parse(value);
      }
      if (!currentUser) throw new Error('No se pudo cargar el usuario. Inicia sesión de nuevo.');
      setUser(currentUser);

      // ⚠️ Ruta corregida: /api/mobile/payments/methods
      const res = await api.get<PaymentMethod[]>('/api/payments/methods', {
        params: { user_id: currentUser.id },
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e: any) {
      setError(e?.message || 'No se pudieron cargar los métodos de pago');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const header = useMemo(() => (
    <div style={{
      background: '#B71C1C', color: 'white', borderRadius: 8, padding: 16,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 18 }}>Mis tarjetas</div>
      </div>
      <button
        onClick={load}
        style={{ background: '#ffffff22', color: 'white', border: 'none', borderRadius: 8, padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 6 }}
        disabled={loading}
        aria-label="Reintentar"
      >
        <RefreshCw size={16} /> Reintentar
      </button>
    </div>
  ), [loading, navigate, load]);

  async function addPaymentMethod(): Promise<string | null> {
    try {
      const user = await SecureStorage.getUser();
      if (!user?.id) throw new Error("Usuario no disponible.");

      const nameParts = (user?.fullName ?? "USUARIO").trim().split(" ");
      const firstName = nameParts[0] || "USUARIO";
      const lastName = nameParts.slice(1).join(" ") || "APELLIDO";
      const email = user?.email || "usuario@example.com";
      const street = user.postAddress || "Calle Falsa 123";
      const city = user.city || "Segovia";
      const postal = user.postCode || "40000";

      const params: Record<string, any> = {
        amount: 1, // céntimos
        currency: "EUR",
        firstName,
        lastName,
        email,
        street,
        city,
        postalCode: postal,
        payer_ref: `user_${user.id}`,
        select_stored_cards: true,
        save_only: true, // ⚠️ custom flag para backend: solo almacenar tarjeta
        user_id: user.id,
      };

      const resp = await api.get("/api/payments/hpp-url", { params });
      if (typeof resp.data === "string") {
        return resp.data;
      }
      return null;
    } catch (err) {
      console.error("Error en addPaymentMethod", err);
      return null;
    }
  }

  return (
    <div style={{ position: 'relative', minHeight: '100vh', background: '#fff' }}>
      <div style={{ padding: 16 }}>
        {header}

        {loading && <p style={{ marginTop: 12 }}>Cargando…</p>}

        {error && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginTop: 12,
            background: '#fdecea', color: '#b24a4a', borderRadius: 8, padding: 12
          }}>
            <AlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div style={{
            marginTop: 24, textAlign: 'center', color: '#666',
            background: '#f7f7f7', borderRadius: 12, padding: 20
          }}>
            No tienes tarjetas guardadas.
          </div>
        )}

        {!loading && !error && items.length > 0 && (
          <div style={{ marginTop: 14, display: 'grid', gap: 10 }} data-tour="user-tarjetas-lista">
            {items.map((pm) => {
              return (
                <div key={`${pm.provider}-${pm.pmt_ref ?? pm.id}`} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 14px', borderRadius: 12, background: '#f7f7f7'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <CreditCard size={22} color="#B71C1C" />
                    <div style={{ display: 'grid' }}>
                      <strong>{pm.brand ?? 'Tarjeta'}</strong>
                      <span style={{ fontSize: 13, color: '#555' }}>
                        •••• {pm.last4 ?? '----'}
                        {pm.exp_month && pm.exp_year ? `  ·  ${String(pm.exp_month).padStart(2, '0')}/${pm.exp_year}` : ''}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {pm.is_default && (
                      <span style={{
                        fontSize: 12, fontWeight: 700, background: '#B71C1C',
                        color: 'white', padding: '4px 8px', borderRadius: 999
                      }}>
                        Predeterminado
                      </span>
                    )}

                    {!pm.is_default && (
                      <button
                        onClick={async () => {
                          try {
                            await api.post('/api/payments/methods/default', {
                              user_id: user?.id,
                              method_id: pm.id,
                            });
                            alert("✅ Tarjeta establecida como predeterminada");
                            load();
                          } catch (err) {
                            console.error(err);
                            alert("❌ Error al establecer predeterminada");
                          }
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#1976d2',
                          fontSize: 13,
                          fontWeight: 600
                        }}
                      >
                        Establecer como predeterminada
                      </button>
                    )}

                    <button
                      onClick={async () => {
                        if (!window.confirm("¿Seguro que quieres eliminar esta tarjeta? Ya no podrás usarla en futuros pagos ni para suscripciones.")) return;
                        try {
                          await api.delete('/api/payments/methods', {
                            params: { user_id: user?.id, method_id: pm.id }
                          });
                          alert("✅ Tarjeta eliminada");
                          load();
                        } catch (err) {
                          console.error(err);
                          alert("❌ Error eliminando tarjeta");
                        }
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: '#B71C1C',
                        fontSize: 13,
                        fontWeight: 600
                      }}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ marginTop: 24, textAlign: "center" }}>
          <button
            data-tour="user-tarjetas-add"
            onClick={async () => {
              alert('🔴¡MUY IMPORTANTE!🔴 Presiona el botón de guardar tarjeta antes de hacer el pago simbólico (0,01€), para poder añadir un nuevo método de pago.\nTranquilo: nosotros no almacenamos tus datos, toda la información queda protegida y guardada de forma segura por CaixaBank.');
              const html = await addPaymentMethod();
              if (html) {
                setPaymentHtml(html);
              }
            }}
            style={{
              padding: "12px 16px",
              background: "#B71C1C",
              color: "white",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
            }}
          >
            ➕ Añadir tarjeta
            (Presiona "Guardar detalles de tarjeta")
          </button>
        </div>
      </div>
      {paymentHtml && (
        <div style={{ marginTop: 20, height: "70vh" }}>
          <PaymentWebView
            html={paymentHtml}
            onSuccess={() => {
              setPaymentHtml(null);
              load(); // recargar métodos
              alert("✅ Tarjeta guardada con éxito");
            }}
            onCancel={() => {
              setPaymentHtml(null);
              alert("❌ Error o cancelación al guardar la tarjeta");
            }}
          />
        </div>
      )}
    </div>
  );

}
